import { arrayMove } from '@dnd-kit/sortable'
import type { FlattenedItem, TreeItem, TreeItems } from './types'

const getDragDepth = (offset: number, indentationWidth: number) => {
    return Math.round(offset / indentationWidth)
}

export const getProjection = <T>(
    items: FlattenedItem<T>[],
    activeId: string,
    overId: string,
    rootId: string | null,
    dragOffset: number,
    indentationWidth: number
) => {
    const overItemIndex = items.findIndex(({ id }) => id === overId)
    const activeItemIndex = items.findIndex(({ id }) => id === activeId)
    const activeItem = items[activeItemIndex]
    const newItems = arrayMove(items, activeItemIndex, overItemIndex)
    const previousItem = newItems[overItemIndex - 1]
    const nextItem = newItems[overItemIndex + 1]
    const dragDepth = getDragDepth(dragOffset, indentationWidth)
    const projectedDepth = activeItem.depth + dragDepth
    const maxDepth = getMaxDepth({
        previousItem,
    })
    const minDepth = getMinDepth({ nextItem })
    let depth = projectedDepth

    if (projectedDepth >= maxDepth) {
        depth = maxDepth
    } else if (projectedDepth < minDepth) {
        depth = minDepth
    }

    const getParentId = () => {
        if (depth === 0 || !previousItem) {
            return rootId
        }

        if (depth === previousItem.depth) {
            return previousItem.parentId
        }

        if (depth > previousItem.depth) {
            return previousItem.id
        }

        const newParent = newItems
            .slice(0, overItemIndex)
            .reverse()
            .find((item) => item.depth === depth)?.parentId

        return newParent ?? null
    }

    return { depth, maxDepth, minDepth, parentId: getParentId() }
}

const getMaxDepth = <T>({ previousItem }: { previousItem: FlattenedItem<T> }) => {
    if (previousItem?.id && Number(previousItem.id)> 0) {
         // Both header and org cannot be under org as a child (can only be under header)
        return previousItem.depth
    } else if (previousItem?.id) {
        return previousItem.depth + 1
    }
    return 0
}

const getMinDepth = <T>({ nextItem }: { nextItem: FlattenedItem<T> }) => {
    if (nextItem) {
        return nextItem.depth
    }
    return 0
}

const flatten = <T>(items: TreeItems<T>, parentId: string | null, depth = 0): FlattenedItem<T>[] => {
    return items.reduce<FlattenedItem<T>[]>((acc, item, index) => {
        return [
            ...acc,
            { ...item, parentId, depth, index },
            ...flatten(item.children, item.id, depth + 1),
        ]
    }, [])
}

export const flattenTree = <T>(items: TreeItems<T>, parentId: string | null): FlattenedItem<T>[] => {
    return flatten(items, parentId)
}

export const buildTree = <T>(flattenedItems: FlattenedItem<T>[], parentId: string | null): TreeItems<T> => {
    const root: TreeItem<T> = { id: parentId || '-999999', children: [] } as any
    const nodes: Record<string, TreeItem<T>> = { [root.id]: root }
    const items = flattenedItems.map((item) => ({ ...item, children: [] }))

    for (const item of items) {
        const { id, children } = item
        const parentId = item.parentId ?? root.id
        const parent = nodes[parentId] ?? findItem(items, parentId)

        nodes[id] = { id, children } as any
        parent?.children.push(item)
    }

    return root.children
}

const findItem = <T>(items: TreeItem<T>[], itemId: string) => {
    return items.find(({ id }) => id === itemId)
}

const findItemDeep = <T>(items: TreeItems<T>, itemId: string): TreeItem<T> | undefined => {
    for (const item of items) {
        const { id, children } = item

        if (id === itemId) {
            return item
        }

        if (children.length) {
            const child = findItemDeep(children, itemId)

            if (child) {
                return child
            }
        }
    }
    return undefined
}

export const setProperty = <T, K extends keyof TreeItem<T>>(
    items: TreeItems<T>,
    id: string,
    property: K,
    setter: (value: TreeItem<T>[K]) => TreeItem<T>[K]
) => {
    for (const item of items) {
        if (item.id === id) {
            item[property] = setter(item[property])
            continue
        }

        if (item.children.length) {
            item.children = setProperty(item.children, id, property, setter)
        }
    }

    return [...items]
}

const countChildren = <T>(items: TreeItem<T>[], count = 0): number => {
    return items.reduce((acc, { children }) => {
        if (children.length) {
            return countChildren(children, acc + 1)
        }

        return acc + 1
    }, count)
}

export const getChildCount = <T>(items: TreeItems<T>, id: string) => {
    const item = findItemDeep(items, id)
    return item ? countChildren(item.children) : 0
}

export const removeChildrenOf = <T>(items: FlattenedItem<T>[], ids: string[]) => {
    const excludeParentIds = [...ids]

    return items.filter((item) => {
        if (item.parentId && excludeParentIds.includes(item.parentId)) {
            if (item.children.length) {
                excludeParentIds.push(item.id)
            }
            return false
        }

        return true
    })
}